import { useEffect, useRef } from 'react';

import { expValEquals } from '@confluence/feature-experiments';
import { useIsEligibleRovoAwareness } from '../entry-points/useIsEligibleRovoAwareness.ts';

type useRovoAwarenessBannerDisplayProps = {
	isModernizeBannerEligible: boolean;
	onboardingTakeoverType: string | null;
	skip?: boolean;
};

/**
 * Custom hook to determine if the Rovo Awareness Banner should be displayed.
 * Uses useRef to persist dismissal state across rerenders without causing
 * unnecessary re-renders of parent components.
 *
 * Shows the banner when:
 * 1. User is eligible for Rovo awareness
 * 2. User is in the experiment test cohort
 * 3. Other banners are not currently visible
 * 4. User has NOT dismissed banners in the current session
 */
export const useRovoAwarenessBannerDisplay = ({
	isModernizeBannerEligible,
	onboardingTakeoverType,
	skip,
}: useRovoAwarenessBannerDisplayProps): boolean => {
	const isRovoAwarenessEligible = useIsEligibleRovoAwareness();
	const hasDismissedBannersThisSessionRef = useRef(false);
	const prevModernizeBannerVisible = useRef(isModernizeBannerEligible);
	const prevOnboardingTakeoverType = useRef(onboardingTakeoverType);

	// Check for dismissals synchronously during render to prevent flash
	if (prevModernizeBannerVisible.current && !isModernizeBannerEligible) {
		hasDismissedBannersThisSessionRef.current = true;
	}
	if (prevOnboardingTakeoverType.current && !onboardingTakeoverType) {
		hasDismissedBannersThisSessionRef.current = true;
	}

	useEffect(() => {
		prevModernizeBannerVisible.current = isModernizeBannerEligible;
	}, [isModernizeBannerEligible]);

	useEffect(() => {
		prevOnboardingTakeoverType.current = onboardingTakeoverType;
	}, [onboardingTakeoverType]);

	const shouldEvaluateExperiments =
		!skip &&
		isRovoAwarenessEligible &&
		!onboardingTakeoverType &&
		!isModernizeBannerEligible &&
		!hasDismissedBannersThisSessionRef.current;

	return shouldEvaluateExperiments && expValEquals('rovo_awareness_experiment', 'cohort', 'test');
};
